import { useEffect, useState } from 'react'

/**
 * `es-select` editor (lot L3) — a select whose options are FETCHED from an
 * endpoint at edit time, instead of a static `options:` list. This keeps a block
 * def **portable**: it stores no instance-specific values (e.g. pipeline names),
 * only the endpoint to read them from.
 *
 * Two modes, one editor :
 *  - **simple** (défaut) — la valeur stockée est une chaîne (la clé de
 *    l'option), qui tombe telle quelle dans un `filter[…]` ES. Un clic
 *    sélectionne, un clic sur l'option DÉJÀ active la désélectionne (`''`).
 *  - **multiple** (`multiple: true`, avec `max:` optionnel) — la valeur stockée
 *    est un tableau d'enveloppes `{ value, label }`, comme les autres éditeurs
 *    de référence du builder (`object-ref`) : le YAML peut alors afficher un
 *    libellé lisible par sélection (Switch), et `ChartLoader` en tire les
 *    valeurs pour des paramètres répétés `filter[champ][]=`. On coche/décoche
 *    autant d'options que voulu, la modale ne se ferme qu'à « Enregistrer ».
 *
 * Rien de sélectionné (`''` ou `[]`) = aucun `filter[…]` envoyé : le widget
 * porte alors sur tout le référentiel (ChartLoader omet les valeurs vides).
 *
 * Field config (from the node's inline `editable:`):
 *   type:            es-select
 *   optionsUrl:      '/api/elasticsearch/deals/config/pipeline_values'
 *   optionsPath:     'aggregations.by_pipeline.buckets'   # dot-path to the array
 *   optionsValueKey: 'key'      # each item's stored value (default 'key')
 *   optionsLabelKey: 'key'      # each item's display label (default = value key)
 *   multiple:        true       # optional — store an array of {value,label}
 *   max:             3          # optional cap when multiple
 *
 * Credentials/CSRF are handled globally by `fetchPatch` for Drupal API routes.
 */
const dig = (obj, path) =>
  (path ? String(path).split('.') : []).reduce(
    (acc, k) => (acc == null ? undefined : acc[k]),
    obj,
  )

export const DynamicSelectEditor = ({
  value,
  optionsUrl,
  optionsPath,
  optionsValueKey = 'key',
  optionsLabelKey,
  multiple = false,
  max,
  onSave,
  onClose,
}) => {
  const [options, setOptions] = useState(null) // null = loading
  const [error, setError] = useState(null)
  // Multi-sélection : brouillon local, committé au « Enregistrer » — la modale
  // se fermerait à chaque `onSave` (contrat ModalFieldEditor), ce qui rendrait
  // impossible de cocher plus d'une option.
  const [selection, setSelection] = useState(() => normalizeIncoming(value))
  const [warning, setWarning] = useState(null)

  useEffect(() => {
    let alive = true
    // Reset before a (re)fetch so a changed optionsUrl shows the loader again and
    // a stale error can't linger over fresh (possibly empty) results.
    setOptions(null)
    setError(null)
    if (!optionsUrl) { setError('optionsUrl manquant'); setOptions([]); return }
    fetch(optionsUrl)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(raw => {
        if (!alive) return
        const arr = optionsPath ? dig(raw, optionsPath) : raw
        const list = (Array.isArray(arr) ? arr : []).map(item => {
          const val = item?.[optionsValueKey]
          return { value: val, label: item?.[optionsLabelKey ?? optionsValueKey] ?? val }
        }).filter(o => o.value != null && o.value !== '')
        setOptions(list)
      })
      .catch(e => { if (alive) { setError(e.message); setOptions([]) } })
    return () => { alive = false }
  }, [optionsUrl, optionsPath, optionsValueKey, optionsLabelKey])

  if (options == null) {
    return <p className="text-sm text-gray-400 py-6 text-center">Chargement des options…</p>
  }
  if (error && options.length === 0) {
    return <p className="text-sm text-red-500 py-6 text-center">Impossible de charger les options ({error}).</p>
  }
  if (options.length === 0) {
    return <p className="text-sm text-gray-400 py-6 text-center">Aucune option disponible.</p>
  }

  const isSelected = (opt) => multiple
    ? selection.some(s => s.value === opt.value)
    : value === opt.value

  // Un clic = bascule. En simple on écrit et on ferme (l'ancien comportement,
  // désélection comprise) ; en multiple on n'écrit que le brouillon local.
  const toggle = (opt) => {
    if (!multiple) {
      onSave(isSelected(opt) ? '' : opt.value)
      onClose()
      return
    }
    setWarning(null)
    setSelection(prev => {
      if (prev.some(s => s.value === opt.value)) {
        return prev.filter(s => s.value !== opt.value)
      }
      if (typeof max === 'number' && prev.length >= max) {
        setWarning(`Limite atteinte : ${max} sélection${max > 1 ? 's' : ''} maximum.`)
        return prev
      }
      return [...prev, { value: opt.value, label: opt.label }]
    })
  }

  const handleSave = () => { onSave(selection); onClose() }
  const handleClear = () => { onSave([]); onClose() }

  return (
    <div className="flex flex-col gap-3">
      {multiple && (
        <p className="text-xs text-gray-500">
          {selection.length === 0
            ? 'Aucune sélection — le widget porte sur tout le référentiel.'
            : `${selection.length}${typeof max === 'number' ? ` / ${max}` : ''} sélectionné${selection.length > 1 ? 's' : ''}.`}
        </p>
      )}

      <div className="flex flex-col gap-2 max-h-[50vh] overflow-y-auto">
        {options.map(opt => {
          const selected = isSelected(opt)
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => toggle(opt)}
              aria-pressed={selected}
              className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg border text-sm text-left transition-all ${
                selected
                  ? 'border-teal-600 bg-teal-50 text-teal-800 font-medium'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700'
              }`}
            >
              <span className="flex-1">{opt.label}</span>
              {selected && (
                <>
                  <span className="flex-shrink-0 text-xs font-normal text-teal-700">Retirer</span>
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-teal-600" />
                </>
              )}
            </button>
          )
        })}
      </div>

      {warning && (
        <p role="status" className="text-xs text-amber-700">{warning}</p>
      )}

      {multiple && (
        <div className="builder-editor-actions builder-editor-actions-split">
          <button
            type="button"
            onClick={handleClear}
            className="btn btn-sm btn-ghost builder-editor-remove"
          >
            Tout vider
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="btn btn-primary"
            >
              Enregistrer
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Coerce an incoming `value` into the internal `{value,label}[]` selection.
 * Tolerates the single-value shape (a bare string) so a block saved before the
 * multi-select switch keeps its filter when its editor is reopened.
 */
function normalizeIncoming(value) {
  if (value == null || value === '') return []
  const arr = Array.isArray(value) ? value : [value]
  return arr
    .map((v) => {
      if (typeof v === 'string') return v === '' ? null : { value: v, label: v }
      if (v && typeof v === 'object' && v.value != null && v.value !== '') {
        return { value: v.value, label: v.label ?? v.value }
      }
      return null
    })
    .filter(Boolean)
}
